import { BreakpointObserver } from '@angular/cdk/layout';
import {
  Component,
  EventEmitter,
  type OnInit,
  Output,
  type OnDestroy,
  inject
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule, Router } from '@angular/router';
import { MsalService } from '@azure/msal-angular';
import { PublicClientApplication } from '@azure/msal-browser';
import { Subject, takeUntil } from 'rxjs';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatListModule } from '@angular/material/list';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatTooltipModule } from '@angular/material/tooltip';
import { HeaderComponent } from '../header/header.component';
import { FooterComponent } from '../footer/footer.component';
import { SpinnerComponent } from '../spinner/spinner.component';
import { SIDENAV_LIST_LABELS } from './sidenav-list.labels';

import { AuthenticationService } from '../../../core/auth/authentication.service';

interface DecodedToken {
  name?: string;
  role?: string;
  userId?: string;
  [key: string]: string | undefined;
}

interface MsalInstanceWithStorage extends PublicClientApplication {
  browserStorage?: { clear(): void };
  nativeInternalStorage?: { clear(): void };
}

@Component({
  selector: 'app-sidenav-list',
  templateUrl: './sidenav-list.component.html',
  styleUrls: ['./sidenav-list.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    MatSidenavModule,
    MatToolbarModule,
    MatListModule,
    MatButtonModule,
    MatIconModule,
    MatMenuModule,
    MatExpansionModule,
    MatTooltipModule,
    HeaderComponent,
    FooterComponent,
    SpinnerComponent
  ]
})
export class SidenavListComponent implements OnInit, OnDestroy {
  private observer = inject(BreakpointObserver);
  private router = inject(Router);
  private authService = inject(AuthenticationService);
  private msal = inject(MsalService);

  labels = SIDENAV_LIST_LABELS;

  @Output() public sidenavToggle = new EventEmitter<void>();

  public userName = '';
  public userRole = '';
  public isMobile = false;
  public UserName = '';
  public AccessUser = '';

  private destroy$ = new Subject<void>();

  ngOnInit(): void {
    this.initializeUserData();
    this.setupResponsiveObserver();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private initializeUserData(): void {
    const token = this.authService.getToken();
    if (!token) {
      this.router.navigateByUrl('/Login');
      return;
    }

    try {
      const decoded = this.authService.decodeToken(token) as DecodedToken;
      if (decoded) {
        const userName =
          decoded['http://schemas.xmlsoap.org/ws/2005/05/identity/claims/name'] ||
          decoded.name ||
          'Unknown User';
        const userRole =
          decoded['http://schemas.microsoft.com/ws/2008/06/identity/claims/role'] ||
          decoded['http://schemas.xmlsoap.org/ws/2005/05/identity/claims/role'] ||
          decoded.role ||
          'Unknown Role';

        this.userName = userName;
        this.userRole = userRole;
        this.UserName = userName;
        this.AccessUser = userRole;
      }
    } catch {
      this.logout();
    }
  }

  private setupResponsiveObserver(): void {
    this.observer
      .observe(['(max-width: 768px)'])
      .pipe(takeUntil(this.destroy$))
      .subscribe(result => {
        this.isMobile = result.matches;
      });
  }

  public onToggleSidenav(): void {
    this.sidenavToggle.emit();
  }

  public logout(): void {
    const isB2C = localStorage.getItem('b2c') === 'true';

    if (isB2C) {
      this.handleB2CLogout();
    } else {
      this.handleStandardLogout();
    }
  }

  public logOut(): void {
    this.logout();
  }

  private handleStandardLogout(): void {
    setTimeout(() => {
      this.authService.logout();
    }, 1000);
  }

  private handleB2CLogout(): void {
    try {
      const msalInstance: MsalInstanceWithStorage = this.msal.instance as MsalInstanceWithStorage;

      try {
        msalInstance.browserStorage?.clear();
        msalInstance.nativeInternalStorage?.clear();
      } catch {
        // Could not clear MSAL storage
      }

      this.authService.clearSession();

      this.msal.logoutPopup().subscribe({
        next: () => {
          this.router.navigateByUrl('/');
        },
        error: () => {
          this.router.navigateByUrl('/');
        }
      });
    } catch {
      this.router.navigateByUrl('/');
    }
  }
}
